import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
} from "@mui/material";
import {
  AddRounded,
  RemoveRounded,
  ArrowForwardRounded,
  RestartAltRounded,
} from "@mui/icons-material";
import { SubscriptionActionFooter } from "./SubscriptionActionDialog";
import {
  trialDate,
  shiftTrialDate,
  formatTrialDate,
} from "./subscriptionTrialDates";

export default function SubscriptionTrialAction({
  sub,
  updating,
  trialReason,
  setTrialReason,
  handleExtendTrial,
  handleResetTrial,
  trialExtendTargetDate,
  setTrialExtendTargetDate,
  trialExtendDateBounds,
  extendDaysForPicker,
  maxTrialReduction,
  trialReduceDateBounds,
  trialReduceTargetDate,
  setTrialReduceTargetDate,
  reduceDaysForPicker,
  handleReduceTrial,
  trialReductionPresets,
  customReductionDays,
  setCustomReductionDays,
}) {
  const [operation, setOperation] = useState("extend");
  const [inputMethod, setInputMethod] = useState("days");
  const [extensionDays, setExtensionDays] = useState(7);
  const isTrial = sub.status === "trialing";
  const isReduction = operation === "reduce";
  const currentEnd = trialDate(sub.trialEnd || sub.trial_end);
  const dateBounds = isReduction
    ? trialReduceDateBounds
    : trialExtendDateBounds;
  const dateSelection = isReduction ? reduceDaysForPicker : extendDaysForPicker;
  const rawDays = isReduction ? customReductionDays : extensionDays;
  const days = inputMethod === "date" ? dateSelection.days : Number(rawDays);
  const validDays =
    Number.isInteger(days) &&
    days >= 1 &&
    days <= (isReduction ? maxTrialReduction : 365);
  const expectedEnd =
    validDays && currentEnd
      ? shiftTrialDate(currentEnd, isReduction ? -days : days)
      : null;
  const unavailableReduction = isReduction && maxTrialReduction < 1;
  const invalidInput =
    inputMethod === "date"
      ? Boolean(isReduction ? trialReduceTargetDate : trialExtendTargetDate) &&
        !validDays
      : rawDays !== "" && rawDays != null && !validDays;
  const disabled = updating || !trialReason.trim() || (isTrial && !expectedEnd);
  const accent = isReduction ? "warning" : "primary";
  const submit = () => {
    if (disabled) return;
    if (!isTrial) handleResetTrial();
    else if (isReduction) handleReduceTrial(days);
    else handleExtendTrial(days);
  };

  return (
    <Stack spacing={2.5}>
      {isTrial ? (
        <>
          <ToggleButtonGroup
            value={operation}
            exclusive
            fullWidth
            size='small'
            aria-label='Trial adjustment'
            disabled={updating}
            onChange={(_, value) => {
              if (!value) return;
              setOperation(value);
              if (
                value === "reduce" &&
                maxTrialReduction >= 1 &&
                !Number(customReductionDays)
              )
                setCustomReductionDays(1);
            }}
            sx={{
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontWeight: 600,
                gap: 0.75,
                py: 1,
              },
            }}
          >
            <ToggleButton value='extend'>
              <AddRounded fontSize='small' />
              Extend trial
            </ToggleButton>
            <ToggleButton value='reduce'>
              <RemoveRounded fontSize='small' />
              Shorten trial
            </ToggleButton>
          </ToggleButtonGroup>

          {!currentEnd ? (
            <Alert severity='warning'>
              The current trial end date is unavailable. Refresh the
              subscription before changing it.
            </Alert>
          ) : unavailableReduction ? (
            <Alert severity='info'>
              This trial cannot be shortened further. Its end date cannot be
              earlier than today.
            </Alert>
          ) : (
            <Stack spacing={1.5}>
              <Stack
                direction='row'
                alignItems='center'
                justifyContent='space-between'
                gap={1}
                flexWrap='wrap'
              >
                <Typography variant='body2' fontWeight={600}>
                  {isReduction ? "Shorten by" : "Extend by"}
                </Typography>
                <ToggleButtonGroup
                  value={inputMethod}
                  exclusive
                  size='small'
                  aria-label='Choose by'
                  disabled={updating}
                  onChange={(_, value) => value && setInputMethod(value)}
                  sx={{
                    "& .MuiToggleButton-root": {
                      textTransform: "none",
                      px: 1.5,
                      py: 0.5,
                    },
                  }}
                >
                  <ToggleButton value='days'>Days</ToggleButton>
                  <ToggleButton value='date'>End date</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
              {inputMethod === "date" ? (
                <TextField
                  fullWidth
                  type='date'
                  size='small'
                  label='New trial end'
                  InputLabelProps={{ shrink: true }}
                  disabled={updating}
                  value={
                    isReduction ? trialReduceTargetDate : trialExtendTargetDate
                  }
                  onChange={(event) =>
                    (isReduction
                      ? setTrialReduceTargetDate
                      : setTrialExtendTargetDate)(event.target.value)
                  }
                  inputProps={{
                    min: dateBounds.minStr || undefined,
                    max: dateBounds.maxStr || undefined,
                  }}
                  error={invalidInput}
                  helperText={
                    invalidInput
                      ? dateSelection.hint
                      : "Choose a date from " +
                        formatTrialDate(dateBounds.minStr) +
                        " to " +
                        formatTrialDate(dateBounds.maxStr) +
                        "."
                  }
                />
              ) : (
                <>
                  <Stack direction='row' gap={1} flexWrap='wrap'>
                    {(isReduction
                      ? trialReductionPresets
                      : [7, 14, 21, 30]
                    ).map((preset) => (
                      <Button
                        key={preset}
                        size='small'
                        color={accent}
                        disableElevation
                        variant={days === preset ? "contained" : "outlined"}
                        aria-pressed={days === preset}
                        disabled={updating}
                        onClick={() =>
                          (isReduction
                            ? setCustomReductionDays
                            : setExtensionDays)(preset)
                        }
                      >
                        {preset} {preset === 1 ? "day" : "days"}
                      </Button>
                    ))}
                  </Stack>
                  <TextField
                    size='small'
                    type='number'
                    fullWidth
                    label='Number of days'
                    disabled={updating}
                    value={rawDays ?? ""}
                    error={invalidInput}
                    onChange={(event) =>
                      (isReduction ? setCustomReductionDays : setExtensionDays)(
                        event.target.value
                      )
                    }
                    inputProps={{
                      min: 1,
                      max: isReduction ? maxTrialReduction : 365,
                      step: 1,
                    }}
                    helperText={
                      isReduction
                        ? "1-" +
                          maxTrialReduction +
                          " whole days. Cannot end before today."
                        : "1-365 whole days."
                    }
                  />
                </>
              )}
            </Stack>
          )}

          <Box
            role='region'
            aria-label='Trial date preview'
            aria-live='polite'
            sx={{
              p: 2,
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
              bgcolor: (theme) => alpha(theme.palette[accent].main, 0.04),
            }}
          >
            <Stack spacing={1.5}>
              <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='center'
                gap={1}
                flexWrap='wrap'
              >
                <Typography variant='body2' fontWeight={600}>
                  Trial end date
                </Typography>
                {expectedEnd && (
                  <Chip
                    size='small'
                    color={accent}
                    variant='outlined'
                    label={
                      days +
                      (days === 1 ? " day " : " days ") +
                      (isReduction ? "earlier" : "later")
                    }
                  />
                )}
              </Stack>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
                  gap: 1.5,
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography variant='caption' color='text.secondary'>
                    Current
                  </Typography>
                  <Typography
                    data-testid='trial-current-date'
                    variant='body1'
                    fontWeight={500}
                  >
                    {formatTrialDate(currentEnd)}
                  </Typography>
                </Box>
                <ArrowForwardRounded
                  sx={{ fontSize: 18, color: "text.secondary" }}
                />
                <Box>
                  <Typography variant='caption' color='text.secondary'>
                    After change
                  </Typography>
                  <Typography
                    data-testid='trial-expected-date'
                    variant='body1'
                    fontWeight={750}
                    color={expectedEnd ? accent + ".main" : "text.secondary"}
                  >
                    {expectedEnd
                      ? formatTrialDate(expectedEnd)
                      : "Not selected"}
                  </Typography>
                </Box>
              </Box>
              <Typography variant='caption' color='text.secondary'>
                {expectedEnd
                  ? "Preview only. Save below to apply."
                  : "Choose a valid adjustment to see the new date."}{" "}
                Billing dates use UTC.
              </Typography>
            </Stack>
          </Box>
        </>
      ) : (
        <Box
          component='section'
          aria-label='Trial reset summary'
          sx={{
            p: 2,
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            bgcolor: "action.hover",
          }}
        >
          <Stack spacing={2}>
            <Stack direction='row' spacing={1} alignItems='center'>
              <RestartAltRounded sx={{ fontSize: 20, color: "primary.main" }} />
              <Typography variant='body2' fontWeight={700}>
                Start a new trial
              </Typography>
            </Stack>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Current status
                </Typography>
                <Typography
                  variant='body1'
                  fontWeight={500}
                  sx={{ textTransform: "capitalize", overflowWrap: "anywhere" }}
                >
                  {sub.status === "past_due"
                    ? "Payment overdue"
                    : (sub.status || "Unknown").replace(/_/g, " ")}
                </Typography>
              </Box>
              <ArrowForwardRounded
                sx={{ fontSize: 18, color: "text.secondary" }}
              />
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  After reset
                </Typography>
                <Typography
                  variant='body1'
                  fontWeight={750}
                  color='primary.main'
                >
                  Trial
                </Typography>
              </Box>
            </Box>
            <Typography variant='body2' color='text.secondary'>
              Reset this subscription to a new trial period. The new end date
              will appear after saving.
            </Typography>
          </Stack>
        </Box>
      )}

      <Stack spacing={1}>
        <Typography
          component='label'
          htmlFor='trial-adjustment-reason'
          variant='body2'
          fontWeight={600}
        >
          Reason
        </Typography>
        <TextField
          id='trial-adjustment-reason'
          fullWidth
          size='small'
          required
          placeholder='Why is the trial being changed?'
          helperText='Saved with the subscription change.'
          value={trialReason}
          onChange={(event) => setTrialReason(event.target.value)}
        />
      </Stack>

      <SubscriptionActionFooter>
        <Button
          variant='contained'
          color={!isTrial || isReduction ? "warning" : "primary"}
          startIcon={
            updating ? <CircularProgress size={16} color='inherit' /> : null
          }
          disabled={disabled}
          onClick={submit}
        >
          {updating
            ? "Saving..."
            : !isTrial
              ? "Reset subscription to trial"
              : isReduction
                ? "Confirm shorter trial"
                : "Confirm trial extension"}
        </Button>
      </SubscriptionActionFooter>
    </Stack>
  );
}
